import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Gift, DeviceMobile, Plus, Trash, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function AdminPricing() {
  const [gc, setGc] = useState("");
  const [air, setAir] = useState("");
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reviewOver, setReviewOver] = useState("100");
  const qc = useQueryClient();

  const { data } = useQuery({ queryKey: ["commerce-pricing"], queryFn: async () => (await api.get("/admin/commerce-pricing")).data });
  const { data: pending } = useQuery({ queryKey: ["gc-admin-orders"], queryFn: async () => (await api.get("/admin/giftcards/orders")).data, refetchInterval: 8000 });

  const review = async (id, action) => {
    try {
      await api.post(`/admin/giftcards/orders/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — issuing now" : "Rejected & refunded");
      qc.invalidateQueries({ queryKey: ["gc-admin-orders"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (data) {
      setGc(String(data.giftcard_markup_pct ?? 0));
      setAir(String(data.airtime_markup_pct ?? 0));
      setBrands((data.brand_markups || []).map((b) => ({ brand: b.brand, pct: String(b.pct) })));
      setReviewOver(String((data.giftcard_review_over_cents ?? 10000) / 100));
    }
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/commerce-pricing", {
        giftcard_markup_pct: parseFloat(gc) || 0,
        airtime_markup_pct: parseFloat(air) || 0,
        giftcard_review_over_cents: Math.round((parseFloat(reviewOver) || 0) * 100),
        brand_markups: brands.filter((b) => b.brand.trim()).map((b) => ({ brand: b.brand.trim(), pct: parseFloat(b.pct) || 0 })),
      });
      toast.success("Pricing updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const gcNum = parseFloat(gc) || 0;
  const airNum = parseFloat(air) || 0;
  const ex = (face, pct) => `$${(face * (1 + (pct ?? gcNum) / 100)).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const addBrand = () => setBrands([...brands, { brand: "", pct: String(gcNum) }]);
  const updBrand = (i, k, v) => setBrands(brands.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const rmBrand = (i) => setBrands(brands.filter((_, j) => j !== i));

  return (
    <div className="space-y-8 text-white" data-testid="admin-pricing-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-semibold"><Coins size={24} weight="fill" /> Gift Card &amp; Airtime Pricing</h1>
        <p className="mt-2 text-sm text-white/60">Customers pay the card's full face value plus the markup you set. Reloadly's reseller discount is your profit on top. Per-brand rates override the global rate.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white/70"><Gift size={16} /> Global gift card markup</h2>
          <div className="mt-4 flex items-center gap-2">
            <input data-testid="pricing-giftcard-markup" type="number" min="0" step="0.5" value={gc}
              onChange={(e) => setGc(e.target.value)}
              className="w-32 rounded-xl border border-white/10 bg-background px-4 py-3 font-mono-metric text-lg outline-none focus:border-primary" />
            <span className="text-lg font-semibold">%</span>
          </div>
          <div className="mt-4 space-y-1 text-sm text-white/70" data-testid="pricing-giftcard-examples">
            <p>$100 card → <span className="font-semibold text-white">{ex(100)}</span></p>
            <p>$1,000 card → <span className="font-semibold text-white">{ex(1000)}</span></p>
            <p>$10,000 card → <span className="font-semibold text-white">{ex(10000)}</span></p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white/70"><DeviceMobile size={16} /> Airtime markup</h2>
          <div className="mt-4 flex items-center gap-2">
            <input data-testid="pricing-airtime-markup" type="number" min="0" step="0.5" value={air}
              onChange={(e) => setAir(e.target.value)}
              className="w-32 rounded-xl border border-white/10 bg-background px-4 py-3 font-mono-metric text-lg outline-none focus:border-primary" />
            <span className="text-lg font-semibold">%</span>
          </div>
          <div className="mt-4 space-y-1 text-sm text-white/70">
            <p>$5 top-up → <span className="font-semibold text-white">${(5 * (1 + airNum / 100)).toFixed(2)}</span></p>
            <p>$20 top-up → <span className="font-semibold text-white">${(20 * (1 + airNum / 100)).toFixed(2)}</span></p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="pricing-brand-overrides">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white/70"><Gift size={16} /> Per-brand / category markups</h2>
          <button data-testid="pricing-add-brand" onClick={addBrand} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/10"><Plus size={14} /> Add brand</button>
        </div>
        <p className="mt-1 text-xs text-white/50">Matches on brand or product name (e.g. "Amazon", "Netflix", "Visa"). First match wins; others use the global rate.</p>
        <div className="mt-4 space-y-2">
          {brands.length === 0 && <p className="text-sm text-white/40">No per-brand overrides — all cards use the global rate.</p>}
          {brands.map((b, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pricing-brand-row-${i}`}>
              <input data-testid={`pricing-brand-name-${i}`} value={b.brand} onChange={(e) => updBrand(i, "brand", e.target.value)}
                placeholder="Brand or product name"
                className="flex-1 rounded-xl border border-white/10 bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input data-testid={`pricing-brand-pct-${i}`} type="number" min="0" step="0.5" value={b.pct} onChange={(e) => updBrand(i, "pct", e.target.value)}
                className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
              <span className="text-sm">%</span>
              <span className="hidden w-28 text-right text-xs text-white/50 sm:inline">$100 → {ex(100, parseFloat(b.pct) || 0)}</span>
              <button data-testid={`pricing-brand-remove-${i}`} onClick={() => rmBrand(i)} className="rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-red-400"><Trash size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="pricing-review">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white/70"><Clock size={16} /> Fraud review — hold high-value cards</h2>
        <p className="mt-1 text-xs text-white/50">Orders above this amount are captured (paid from the customer's wallet) but held as <b>pending review</b> until you approve them. Set 0 to review every order.</p>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-sm text-white/60">Review orders over $</span>
          <input data-testid="pricing-review-threshold" type="number" min="0" value={reviewOver} onChange={(e) => setReviewOver(e.target.value)}
            className="w-32 rounded-xl border border-white/10 bg-background px-4 py-2.5 font-mono-metric text-sm outline-none focus:border-primary" />
        </div>
        <div className="mt-4 space-y-2" data-testid="pricing-pending-list">
          {(pending?.orders || []).filter((o) => o.status === "pending_review").length === 0 &&
            <p className="text-sm text-white/40">No gift cards awaiting review.</p>}
          {(pending?.orders || []).filter((o) => o.status === "pending_review").map((o) => (
            <div key={o.id} data-testid={`gc-review-${o.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3">
              <div className="min-w-0 text-sm">
                <p className="font-semibold">{o.product_name} · {o.recipient_denom} {o.recipient_currency} × {o.quantity}</p>
                <p className="text-xs text-white/50">{o.recipient_email} · ${(o.price_cents / 100).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`gc-approve-${o.id}`} onClick={() => review(o.id, "approve")} className="flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white"><CheckCircle size={14} weight="fill" /> Approve</button>
                <button data-testid={`gc-reject-${o.id}`} onClick={() => review(o.id, "reject")} className="flex items-center gap-1 rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold hover:bg-white/10"><XCircle size={14} /> Reject &amp; refund</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="pricing-save" onClick={save} disabled={saving}
        className="rounded-full bg-primary px-8 py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
        {saving ? "Saving…" : "Save pricing"}
      </button>
    </div>
  );
}
